import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { OnboardingChecklist } from "@/components/app/onboarding-checklist";
import { QueueHealthWidget } from "@/components/app/queue-health-widget";
import { TrackingQueueStatus } from "@/components/app/tracking-queue-status";
import { PlacementStatusLegend } from "@/components/app/placement-status-legend";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowUpRight,
  Send,
  Eye,
  MousePointerClick,
  AlertTriangle,
  Bot,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: Dashboard,
  head: () => ({ meta: [{ title: "Dashboard — InboxPulse" }] }),
});

const PROVIDERS = ["Gmail", "Outlook", "Yahoo", "GMX", "T-Online"];

function Dashboard() {
  const queryClient = useQueryClient();
  const [selectedCampaign, setSelectedCampaign] = useState<string>("all");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: campaignList } = useQuery({
    queryKey: ["dashboard-campaign-list"],
    queryFn: async () => {
      const { data } = await supabase
        .from("campaigns")
        .select("id,name")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats", selectedCampaign],
    queryFn: async () => {
      const campaignFilter = selectedCampaign !== "all" ? selectedCampaign : null;
      let campaignsQuery = supabase
        .from("campaigns")
        .select("sent_count,open_count,click_count,bounce_count,complaint_count,spam_score");
      if (campaignFilter) campaignsQuery = campaignsQuery.eq("id", campaignFilter);
      let eventsQuery = supabase
        .from("tracking_events")
        .select("event_type,provider,occurred_at,device,country,metadata")
        .order("occurred_at", { ascending: false })
        .limit(500);
      if (campaignFilter) eventsQuery = eventsQuery.eq("campaign_id", campaignFilter);
      // Separate, größere Abfrage nur für Opens, damit der Unique-Count
      // möglichst vollständig ist (anders als der 500er-Mischpool oben).
      let openEventsQuery = supabase
        .from("tracking_events")
        .select("provider,device,ip,metadata")
        .eq("event_type", "open")
        .order("occurred_at", { ascending: false })
        .limit(5000);
      if (campaignFilter) openEventsQuery = openEventsQuery.eq("campaign_id", campaignFilter);
      const [c, s, d, e] = await Promise.all([
        campaignsQuery,
        supabase.from("senders").select("id,health_score"),
        supabase.from("domains").select("id,reputation_score"),
        eventsQuery,
      ]);
      const openEventsRes = await openEventsQuery;
      const since24h = new Date(Date.now() - 24 * 3_600_000).toISOString();
      let clicksQuery = supabase
        .from("ot_clicks")
        .select("is_bot,bot_reason,occurred_at")
        .gte("occurred_at", since24h)
        .order("occurred_at", { ascending: false })
        .limit(2000);
      if (campaignFilter) clicksQuery = clicksQuery.eq("campaign_id", campaignFilter);
      const { data: clickData } = await clicksQuery;
      let placementQuery = supabase
        .from("placement_results")
        .select("placement")
        .gte("created_at", since24h)
        .limit(5000);
      if (campaignFilter) placementQuery = placementQuery.eq("campaign_id", campaignFilter);
      const { data: placementData } = await placementQuery;
      const placementCounts = { promotions: 0, spam: 0, blocked: 0, delayed: 0 };
      for (const row of placementData ?? []) {
        const placement = (row.placement ?? "").toLowerCase();
        if (placement === "promotions") placementCounts.promotions++;
        else if (placement === "spam") placementCounts.spam++;
        else if (["blocked", "rejected", "bounced"].includes(placement)) placementCounts.blocked++;
        else if (["delayed", "deferred", "pending"].includes(placement)) placementCounts.delayed++;
      }
      const sums = (c.data ?? []).reduce(
        (acc, x) => ({
          sent: acc.sent + (x.sent_count ?? 0),
          opens: acc.opens + (x.open_count ?? 0),
          clicks: acc.clicks + (x.click_count ?? 0),
          bounces: acc.bounces + (x.bounce_count ?? 0),
          complaints: acc.complaints + (x.complaint_count ?? 0),
        }),
        { sent: 0, opens: 0, clicks: 0, bounces: 0, complaints: 0 },
      );
      // Unique-Opens: ein Empfänger zählt 1×. Wir nehmen die stabilste
      // verfügbare Empfänger-Kennung (Token > Hash > IP-Fingerprint).
      const uniqueRecipientSet = new Set<string>();
      let openRowsScanned = 0;
      for (const ev of openEventsRes.data ?? []) {
        openRowsScanned++;
        const meta = (ev as { metadata?: Record<string, unknown> | null }).metadata ?? {};
        const recipientToken = typeof meta.recipientToken === "string" ? meta.recipientToken : null;
        const recipientHash = typeof meta.recipientHash === "string" ? meta.recipientHash : null;
        const recipientLabel = typeof meta.recipientLabel === "string" ? meta.recipientLabel : null;
        let key: string;
        if (recipientToken) key = `t:${recipientToken}`;
        else if (recipientHash) key = `h:${recipientHash}`;
        else if (recipientLabel) key = `l:${recipientLabel}`;
        else {
          const provider = (ev as { provider?: string | null }).provider ?? "";
          const device = (ev as { device?: string | null }).device ?? "";
          const ip = (ev as { ip?: string | null }).ip ?? "";
          key = `f:${provider}|${device}|${ip}`;
        }
        uniqueRecipientSet.add(key);
      }
      const uniqueOpens = uniqueRecipientSet.size;
      const uniqueCapped = openRowsScanned >= 5000;
      return {
        ...sums,
        uniqueOpens,
        uniqueCapped,
        senders: s.data?.length ?? 0,
        domains: d.data?.length ?? 0,
        avgReputation: d.data?.length
          ? Math.round(d.data.reduce((a, x) => a + (x.reputation_score ?? 0), 0) / d.data.length)
          : 0,
        events: e.data ?? [],
        clicks24h: clickData ?? [],
        placementCounts,
      };
    },
  });

  // realtime
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const ch = supabase
      .channel("dash-events")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "tracking_events" },
        () => {
          setTick((t) => t + 1);
          queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
        },
      )
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "ot_clicks" }, () =>
        setTick((t) => t + 1),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [queryClient]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      await queryClient.refetchQueries({ queryKey: ["dashboard-stats", selectedCampaign] });
      const label =
        selectedCampaign === "all"
          ? "alle Kampagnen"
          : (campaignList?.find((c) => c.id === selectedCampaign)?.name ?? "Kampagne");
      toast.success(`Daten aktualisiert: ${label}`);
    } catch (err) {
      toast.error("Aktualisierung fehlgeschlagen");
      console.error(err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // bucket events into last 24 hours
  const series = (() => {
    const buckets: Record<string, number> = {};
    const now = Date.now();
    for (let i = 23; i >= 0; i--) {
      const d = new Date(now - i * 3_600_000);
      buckets[d.toISOString().slice(0, 13)] = 0;
    }
    for (const ev of stats?.events ?? []) {
      const k = (ev.occurred_at ?? "").slice(0, 13);
      if (k in buckets) buckets[k]++;
    }
    return Object.entries(buckets).map(([k, v]) => ({ hour: k.slice(11), opens: v }));
  })();

  const providerCounts = PROVIDERS.map((p) => ({
    provider: p,
    inbox: (stats?.events ?? []).filter((e) => (e.provider ?? "").toLowerCase() === p.toLowerCase())
      .length,
  }));

  // Stealth-Tracking (Quelle: i-pixel)
  const stealthEvents = (stats?.events ?? []).filter((e) => {
    const meta = (e as { metadata?: { source?: string } }).metadata;
    return (
      (meta?.source === "i-pixel" || meta?.source === "q-pixel") && (e.event_type ?? "") === "open"
    );
  });
  const stealthProviders = (() => {
    const counts: Record<string, number> = {};
    for (const ev of stealthEvents) {
      const p = ev.provider || "Unknown";
      counts[p] = (counts[p] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([provider, count]) => ({ provider, count }))
      .sort((a, b) => b.count - a.count);
  })();
  const stealthDevices = (() => {
    const counts: Record<string, number> = {};
    for (const ev of stealthEvents) {
      const dev = (ev as { device?: string | null }).device || "unknown";
      counts[dev] = (counts[dev] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([device, count]) => ({ device, count }))
      .sort((a, b) => b.count - a.count);
  })();
  const stealthCountries = (() => {
    const counts: Record<string, number> = {};
    for (const ev of stealthEvents) {
      const co = (ev as { country?: string | null }).country || "??";
      counts[co] = (counts[co] ?? 0) + 1;
    }
    return Object.entries(counts)
      .map(([country, count]) => ({ country, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  })();

  const openRate = stats?.sent ? Math.round((stats.opens / stats.sent) * 100) : 0;
  const uniqueOpens = stats?.uniqueOpens ?? 0;
  const totalOpens = stats?.opens ?? 0;
  const uniqueShare = totalOpens > 0 ? Math.round((uniqueOpens / totalOpens) * 100) : 0;
  const bounceRate = stats?.sent ? Math.round((stats.bounces / stats.sent) * 100) : 0;

  // Bot-Klick-Analytics (letzte 24 h)
  const clicks24h = stats?.clicks24h ?? [];
  const botClicks24h = clicks24h.filter((c) => c.is_bot).length;
  const totalClicks24h = clicks24h.length;
  const botShare = totalClicks24h > 0 ? Math.round((botClicks24h / totalClicks24h) * 100) : 0;
  const botReasons = clicks24h.reduce<Record<string, number>>((acc, c) => {
    if (!c.is_bot) return acc;
    const r = c.bot_reason ?? "unknown";
    acc[r] = (acc[r] ?? 0) + 1;
    return acc;
  }, {});
  const reasonRows = Object.entries(botReasons).sort((a, b) => b[1] - a[1]);
  const botSeries = (() => {
    const buckets: Record<string, { bot: number; real: number }> = {};
    const now = Date.now();
    for (let i = 23; i >= 0; i--) {
      const d = new Date(now - i * 3_600_000);
      buckets[d.toISOString().slice(0, 13)] = { bot: 0, real: 0 };
    }
    for (const c of clicks24h) {
      const k = (c.occurred_at ?? "").slice(0, 13);
      if (k in buckets) {
        if (c.is_bot) buckets[k].bot++;
        else buckets[k].real++;
      }
    }
    return Object.entries(buckets).map(([k, v]) => ({ hour: k.slice(11), ...v }));
  })();

  const kpis = [
    { label: "Sent", value: stats?.sent ?? 0, icon: Send, accent: "text-primary" },
    {
      label: "Total Opens",
      value: totalOpens,
      sub: `${openRate}% open rate`,
      icon: Eye,
      accent: "text-success",
    },
    {
      label: "Unique Opens",
      value: uniqueOpens,
      sub:
        totalOpens > 0
          ? `${uniqueShare}% von Total${stats?.uniqueCapped ? " · gekappt @5k" : ""}`
          : "—",
      icon: EyeOff,
      accent: "text-success",
    },
    { label: "Clicks", value: stats?.clicks ?? 0, icon: MousePointerClick, accent: "text-warning" },
    {
      label: "Bounces",
      value: stats?.bounces ?? 0,
      sub: `${bounceRate}% bounce`,
      icon: AlertTriangle,
      accent: "text-destructive",
    },
    {
      label: "Bot-Klicks 24h",
      value: botClicks24h,
      sub: `${botShare}% aller Klicks`,
      icon: Bot,
      accent: botShare > 50 ? "text-destructive" : "text-warning",
    },
  ];

  return (
    <div className="p-8" data-tick={tick}>
      <PageHeader
        title="Deliverability overview"
        description="Live engagement signals across all your campaigns, senders, and domains."
      />

      <Card className="p-4 mb-6 bg-card/60 border-border flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">Kampagne:</span>
        <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="Kampagne wählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kampagnen</SelectItem>
            {(campaignList ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={handleRefresh} disabled={isRefreshing} size="sm" variant="secondary">
          <RefreshCw className={`size-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
          {isRefreshing ? "Lade…" : "Jetzt neu laden"}
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">
          Lädt frische Tracking-Daten direkt aus der Datenbank.
        </span>
      </Card>

      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {kpis.map((k) => (
          <Card key={k.label} className="border-border bg-card/70 p-4">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {k.label}
              </span>
              <k.icon className={`size-4 shrink-0 ${k.accent}`} />
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums tracking-tight">
              {k.value.toLocaleString()}
            </div>
            {k.sub && <div className="mt-0.5 truncate text-xs text-muted-foreground">{k.sub}</div>}
          </Card>
        ))}
      </div>

      <Card className="mb-6 border-border bg-card/60 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">Zustellsignale · letzte 24 Stunden</h2>
            <p className="text-xs text-muted-foreground">
              Fahre über einen Status, um Bedeutung und nächste Schritte zu sehen.
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-success">
            <span className="size-1.5 rounded-full bg-success animate-pulse" /> live
          </span>
        </div>
        <PlacementStatusLegend counts={stats?.placementCounts} />
      </Card>

      <OnboardingChecklist />

      <TrackingQueueStatus campaignId={selectedCampaign} />

      <QueueHealthWidget />

      <HelpPanel
        title="Hilfe: Übersicht / Dashboard"
        what={
          <>
            Das Dashboard ist deine Live-Übersicht uber alle E-Mail-Kampagnen. Es zeigt, wie viele
            Mails verschickt, geoffnet und geklickt wurden, wie viele zuruckkamen (Bounces) und wie
            viele Klicks von Bots stammen.
          </>
        }
        why={
          <>
            Hier siehst du auf einen Blick, ob deine Zustellbarkeit gesund ist. Sinkt die
            Offnungsrate oder steigen Bounces/Bot-Klicks, erkennst du Probleme fruh - bevor ganze
            Kampagnen im Spam landen.
          </>
        }
        how={[
          <>Pruf zuerst die funf Kennzahlen oben: Sent, Opens, Clicks, Bounces, Bot-Klicks.</>,
          <>Schau dir den Graph "Engagement last 24 hours" an - sind die Offnungen stabil?</>,
          <>
            Vergleiche die Provider-Verteilung (Gmail, Outlook, ...) - kein Provider sollte 0
            zeigen.
          </>,
          <>
            Wirf einen Blick auf "Bot- vs. echte Klicks": ein hoher Bot-Anteil verzerrt deine
            Klickrate.
          </>,
          <>
            Bei Auffalligkeiten gehe in die Detailseiten (Kampagnen, Domains, Alarme) und untersuche
            weiter.
          </>,
        ]}
        tips={[
          <>Eine Open-Rate uber 20% gilt als gut, uber 30% als sehr gut.</>,
          <>Bounce-Rate sollte dauerhaft unter 2% bleiben - sonst Listenpflege starten.</>,
          <>Wenn Bot-Klicks uber 50% liegen, pruf deine Klick-Tracking-Konfiguration.</>,
          <>
            Die Reputation (rechts oben) ist ein Mittelwert uber alle Domains - einzelne schwache
            Domains sieht man besser unter "Domains".
          </>,
        ]}
        glossary={[
          { term: "Sent", def: "Anzahl tatsachlich versendeter E-Mails." },
          {
            term: "Open-Rate",
            def: "Anteil der Empfanger, die die Mail geoffnet haben (Pixel geladen).",
          },
          {
            term: "Bounce",
            def: "Mail konnte nicht zugestellt werden (z.B. Adresse existiert nicht).",
          },
          {
            term: "Complaint",
            def: "Empfanger hat 'Als Spam markieren' geklickt - sehr schadlich fur die Reputation.",
          },
          {
            term: "Reputation",
            def: "Vertrauenswert (0-100), den Provider deinen Domains zuordnen.",
          },
          {
            term: "Bot-Klick",
            def: "Automatisch ausgeloster Klick (z.B. von Spamfiltern), nicht von einem Menschen.",
          },
          { term: "Provider", def: "Mail-Anbieter des Empfangers, z.B. Gmail, Outlook, GMX." },
        ]}
      />

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2 p-6 bg-card/60 border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold">Engagement last 24 hours</h3>
              <p className="text-xs text-muted-foreground">Tracking pixel events per hour</p>
            </div>
            <span className="inline-flex items-center gap-1 text-xs text-success">
              <span className="size-1.5 rounded-full bg-success animate-pulse" /> live
            </span>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="oklch(0.62 0.21 275)" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="oklch(0.62 0.21 275)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="oklch(0.28 0.05 270)" strokeDasharray="3 3" />
                <XAxis dataKey="hour" stroke="oklch(0.7 0.03 270)" fontSize={11} />
                <YAxis stroke="oklch(0.7 0.03 270)" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "oklch(0.19 0.05 270)",
                    border: "1px solid oklch(0.28 0.05 270)",
                    borderRadius: 8,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="opens"
                  stroke="oklch(0.62 0.21 275)"
                  fill="url(#g)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 bg-card/60 border-border">
          <h3 className="font-semibold">Reputation</h3>
          <p className="text-xs text-muted-foreground mb-6">
            Average across {stats?.domains ?? 0} domains
          </p>
          <div className="text-5xl font-semibold tracking-tight">
            {stats?.avgReputation ?? 0}
            <span className="text-lg text-muted-foreground">/100</span>
          </div>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Senders</span>
              <span>{stats?.senders ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Complaints</span>
              <span className="text-destructive">{stats?.complaints ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Open rate</span>
              <span className="text-success">{openRate}%</span>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6 bg-card/60 border-border">
        <div className="mb-4">
          <h3 className="font-semibold">Provider distribution</h3>
          <p className="text-xs text-muted-foreground">
            Inbox events per mailbox provider <ArrowUpRight className="size-3 inline" />
          </p>
        </div>
        <div className="h-56">
          <ResponsiveContainer>
            <BarChart data={providerCounts}>
              <CartesianGrid stroke="oklch(0.28 0.05 270)" strokeDasharray="3 3" />
              <XAxis dataKey="provider" stroke="oklch(0.7 0.03 270)" fontSize={11} />
              <YAxis stroke="oklch(0.7 0.03 270)" fontSize={11} />
              <Tooltip
                contentStyle={{
                  background: "oklch(0.19 0.05 270)",
                  border: "1px solid oklch(0.28 0.05 270)",
                  borderRadius: 8,
                }}
              />
              <Bar dataKey="inbox" fill="oklch(0.62 0.21 275)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <Card className="lg:col-span-2 p-6 bg-card/60 border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <Bot className="size-4" /> Bot- vs. echte Klicks (24 h)
              </h3>
              <p className="text-xs text-muted-foreground">
                Live-Klassifikation per Geschwindigkeit, UA und IP-Burst
              </p>
            </div>
            <span className="text-xs text-muted-foreground">
              {totalClicks24h.toLocaleString()} Klicks · {botShare}% Bots
            </span>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={botSeries}>
                <CartesianGrid stroke="oklch(0.28 0.05 270)" strokeDasharray="3 3" />
                <XAxis dataKey="hour" stroke="oklch(0.7 0.03 270)" fontSize={11} />
                <YAxis stroke="oklch(0.7 0.03 270)" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "oklch(0.19 0.05 270)",
                    border: "1px solid oklch(0.28 0.05 270)",
                    borderRadius: 8,
                  }}
                />
                <Bar
                  dataKey="real"
                  stackId="a"
                  fill="oklch(0.7 0.18 150)"
                  radius={[0, 0, 0, 0]}
                  name="Echt"
                />
                <Bar
                  dataKey="bot"
                  stackId="a"
                  fill="oklch(0.65 0.2 25)"
                  radius={[6, 6, 0, 0]}
                  name="Bot"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-6 bg-card/60 border-border">
          <h3 className="font-semibold">Bot-Gründe</h3>
          <p className="text-xs text-muted-foreground mb-4">
            Verteilung der Erkennungs-Heuristiken
          </p>
          {reasonRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Keine Bot-Klicks in den letzten 24 h.</p>
          ) : (
            <div className="space-y-3">
              {reasonRows.map(([reason, count]) => {
                const pct = botClicks24h > 0 ? Math.round((count / botClicks24h) * 100) : 0;
                const label: Record<string, string> = {
                  too_fast: "Zu schnell (Pre-Scanner)",
                  ua_blacklist: "Scanner-User-Agent",
                  dedup: "Wiederholter Klick",
                  ip_burst: "IP-Burst",
                  ua_suspicious: "Verdächtiger UA",
                  missing_accept: "Fehlende Header",
                  unknown: "Unbekannt",
                };
                return (
                  <div key={reason}>
                    <div className="flex justify-between text-xs mb-1">
                      <span>{label[reason] ?? reason}</span>
                      <span className="text-muted-foreground">
                        {count} · {pct}%
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-destructive" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6 bg-card/60 border-border mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <EyeOff className="size-4" /> Stealth-Tracking (Quelle: i-pixel)
            </h3>
            <p className="text-xs text-muted-foreground">
              Opens über den getarnten Pixel — gefiltert nach metadata.source = "i-pixel" (letzte
              500 Events).
            </p>
          </div>
          <span className="text-xs text-muted-foreground">
            {stealthEvents.length.toLocaleString()} Opens
          </span>
        </div>

        {stealthEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Noch keine Stealth-Opens registriert. Sobald der i-Pixel in einer Mail geladen wird,
            erscheinen die Events hier live.
          </p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                Nach Provider
              </h4>
              <div className="space-y-2">
                {stealthProviders.map((row) => {
                  const pct = Math.round((row.count / stealthEvents.length) * 100);
                  return (
                    <div key={row.provider}>
                      <div className="flex justify-between text-xs mb-1">
                        <span>{row.provider}</span>
                        <span className="text-muted-foreground">
                          {row.count} · {pct}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                Nach Gerät
              </h4>
              <div className="space-y-2">
                {stealthDevices.map((row) => {
                  const pct = Math.round((row.count / stealthEvents.length) * 100);
                  return (
                    <div key={row.device}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="capitalize">{row.device}</span>
                        <span className="text-muted-foreground">
                          {row.count} · {pct}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-success" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                Top-Länder
              </h4>
              <div className="space-y-2">
                {stealthCountries.map((row) => {
                  const pct = Math.round((row.count / stealthEvents.length) * 100);
                  return (
                    <div key={row.country}>
                      <div className="flex justify-between text-xs mb-1">
                        <span>{row.country}</span>
                        <span className="text-muted-foreground">
                          {row.count} · {pct}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-warning" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
